import React, { useMemo, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { matchKeywordCount } from './questionScoring';

// PrescriptionEdit: student modifies existing chart entries (renal-adjusted
// doses, hepatic switches, frequency reductions). Each row pre-populates
// from the on-chart prescription; student edits dose / frequency / route /
// indication notes. Engine grade against keyword-presence per field.
//
// Schema:
// {
//   questionType: 'PrescriptionEdit',
//   rxEdits: [
//     {
//       drug: 'Atenolol',          // matches an entry on chart by name (substring)
//       expectedDoseKeywords: ['25', 'mg'],
//       expectedFrequencyKeywords: ['once', 'daily'],
//       expectedRouteKeywords: [],
//       expectedNoteKeywords: ['renal', 'reduce'],
//       modelExplanation: 'Halve dose to 25mg OD - renally cleared, eGFR 22'
//     }
//   ]
// }

const FIELDS = [
  ['dose', 'expectedDoseKeywords', 'Dose'],
  ['frequency', 'expectedFrequencyKeywords', 'Frequency'],
  ['route', 'expectedRouteKeywords', 'Route'],
  ['note', 'expectedNoteKeywords', 'Reason / note'],
];

const findChartEntry = (chartList, drugName) => {
  if (!Array.isArray(chartList) || !drugName) return null;
  const needle = String(drugName).toLowerCase().trim();
  return chartList.find((rx) => {
    const drug = String(rx?.drug || '').toLowerCase().trim();
    return drug === needle || drug.startsWith(needle) || needle.startsWith(drug);
  }) || null;
};

const PrescriptionEdit = ({ question, prescriptions = [] }) => {
  const rxEdits = useMemo(() => Array.isArray(question?.rxEdits) ? question.rxEdits : [], [question]);
  const [edits, setEdits] = useState(() => rxEdits.map((rx) => {
    const chart = findChartEntry(prescriptions, rx.drug);
    return {
      dose: chart?.dose || '',
      frequency: chart?.frequency || '',
      route: chart?.route || '',
      note: '',
    };
  }));
  const [submitted, setSubmitted] = useState(false);

  const update = (idx, field, val) => {
    setEdits((curr) => curr.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  };

  const results = useMemo(() => {
    if (!submitted) return null;
    return rxEdits.map((expected, idx) => {
      const submittedRow = edits[idx];
      const fieldResults = FIELDS.map(([field, kwField, label]) => {
        const kws = expected[kwField] || [];
        const hits = matchKeywordCount(submittedRow[field], kws);
        return { field, label, hits, target: kws.length, ok: kws.length === 0 || hits >= Math.ceil(kws.length / 2) };
      });
      const totalHits = fieldResults.reduce((s, f) => s + f.hits, 0);
      const totalTargets = fieldResults.reduce((s, f) => s + f.target, 0);
      const allOk = fieldResults.every((f) => f.ok);
      return { fieldResults, totalHits, totalTargets, allOk };
    });
  }, [submitted, rxEdits, edits]);

  const overallCorrect = results ? results.every((r) => r.allOk) : false;

  const canSubmit = !submitted && edits.length > 0;

  return (
    <Container className="mt-3 p-3 bg-light text-dark rounded prescription-edit-question">
      <h3 className="d-flex flex-wrap align-items-center gap-2">
        Q{question.questionNumber}: {question.questionTitle}
        {submitted ? (
          overallCorrect
            ? <Badge bg="success">All edits correct</Badge>
            : <Badge bg="warning" text="dark">{results.filter((r) => r.allOk).length} / {rxEdits.length} fully correct</Badge>
        ) : null}
      </h3>
      <hr />
      <p className="mb-3">{question.questionText}</p>

      {rxEdits.map((rx, idx) => {
        const e = edits[idx];
        const r = results ? results[idx] : null;
        return (
          <div key={`edit-${idx}`} className="prescription-edit-question__row mb-3 p-2 border rounded bg-white">
            <strong>{rx.drug}</strong>
            <Row className="g-2 mt-1">
              <Col xs={6} md={3}>
                <Form.Label className="small mb-1">Dose</Form.Label>
                <Form.Control type="text" size="sm" value={e.dose} disabled={submitted} onChange={(ev) => update(idx, 'dose', ev.target.value)} />
              </Col>
              <Col xs={6} md={3}>
                <Form.Label className="small mb-1">Frequency</Form.Label>
                <Form.Control type="text" size="sm" value={e.frequency} disabled={submitted} onChange={(ev) => update(idx, 'frequency', ev.target.value)} />
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="small mb-1">Route</Form.Label>
                <Form.Control type="text" size="sm" value={e.route} disabled={submitted} onChange={(ev) => update(idx, 'route', ev.target.value)} />
              </Col>
              <Col xs={12} md={4}>
                <Form.Label className="small mb-1">Reason / note</Form.Label>
                <Form.Control type="text" size="sm" placeholder="e.g. renal-adjusted, eGFR 22" value={e.note} disabled={submitted} onChange={(ev) => update(idx, 'note', ev.target.value)} />
              </Col>
            </Row>
            {submitted && r ? (
              <div className="mt-2 small">
                {r.fieldResults.filter((f) => f.target > 0).map((f) => (
                  <Badge key={f.field} bg={f.ok ? 'success' : 'warning'} text={f.ok ? 'light' : 'dark'} className="me-2">
                    {f.label} {f.hits}/{f.target}
                  </Badge>
                ))}
                {!r.allOk && rx.modelExplanation ? (
                  <div className="text-muted mt-1"><strong>Model:</strong> {rx.modelExplanation}</div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}

      {!submitted ? (
        <Button type="button" variant="primary" disabled={!canSubmit} onClick={() => setSubmitted(true)}>
          Submit edits
        </Button>
      ) : null}
    </Container>
  );
};

export default PrescriptionEdit;
